import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { Brewery, getBreweryByName } from '../../data/breweries';

interface BreweryDetailContextValue {
  selectedBrewery: Brewery | null;
  isOpen: boolean;
  openBreweryDetail: (breweryName: string) => boolean;
  closeBreweryDetail: () => void;
}

const BreweryDetailContext = createContext<BreweryDetailContextValue | undefined>(undefined);

interface BreweryDetailProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component for managing brewery detail page state.
 * Allows any component to open/close the brewery detail view.
 */
export const BreweryDetailProvider: React.FC<BreweryDetailProviderProps> = ({ children }) => {
  const [selectedBrewery, setSelectedBrewery] = useState<Brewery | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const openBreweryDetail = useCallback((breweryName: string) => {
    const brewery = getBreweryByName(breweryName.trim());
    if (brewery) {
      setSelectedBrewery(brewery);
      setIsOpen(true);
      return true;
    } else {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(`Brewery not found: ${breweryName}`);
      }
      return false;
    }
  }, []);

  const closeBreweryDetail = useCallback(() => {
    setIsOpen(false);
    // Delay clearing the brewery to allow for exit animations
    setTimeout(() => {
      setSelectedBrewery(null);
    }, 300);
  }, []);

  const value = useMemo(() => ({
    selectedBrewery,
    isOpen,
    openBreweryDetail,
    closeBreweryDetail,
  }), [selectedBrewery, isOpen, openBreweryDetail, closeBreweryDetail]);

  return (
    <BreweryDetailContext.Provider value={value}>
      {children}
    </BreweryDetailContext.Provider>
  );
};

/**
 * Hook to access brewery detail functionality.
 * Must be used within a BreweryDetailProvider.
 */
export const useBreweryDetail = (): BreweryDetailContextValue => {
  const context = useContext(BreweryDetailContext);
  if (context === undefined) {
    throw new Error('useBreweryDetail must be used within a BreweryDetailProvider');
  }
  return context;
};
